import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { isString } from 'underscore';
import PropTypes from 'prop-types';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';

export const CallToActionAriaText = forwardRef(({ i18n, value }, ref) => {
  const textFieldRef = React.useRef(null); // Create a ref for the TextField
  const [errorMessage, setErrorMessage] = React.useState('');
  const { maxLength } = ResponsiveAd.properties.CallToActionAriaText;

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const getErrorMessage = (newValue) => {
    if (isString(newValue)) {
      if (newValue.length > maxLength) {
        return i18n.getString(_TL_('Value cannot be longer than 30 characters'));
      }
      return '';
    }
    return '';
  };

  useImperativeHandle(ref, () => ({
    getData: () => textFieldRef.current.value.trim(),
    isValid: () => {
      const newValue = textFieldRef.current.value.trim();
      const message = getErrorMessage(newValue);
      setErrorMessage(message);
      return message === '';
    },
  }));

  const handleChange = (event, [newValue]) => {
    setErrorMessage(getErrorMessage(newValue));
  };

  return (
    <RemainingCharsValidatedTextField
      value={value}
      i18n={i18n}
      label={i18n.getString(_TL_('Aria text'))}
      placeholder={i18n.getString(_TL_('Enter aria text'))}
      componentRef={textFieldRef}
      errorMessage={errorMessage}
      onChange={handleChange}
      validationProps={{
        schema: ResponsiveAd,
        field: 'CallToActionAriaText',
        alwaysShowError: true,
        i18n: legacyI18n,
        convertor: {
          fromModel: field => (field === 'delete_value' ? '' : field),
        },
      }}
    />
  );
});

CallToActionAriaText.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  value: PropTypes.string,
};

CallToActionAriaText.defaultProps = {
  value: '',
};
